import { randomUUID } from 'node:crypto';

import type { Page, RequestOptions } from '../types/common.js';
import { VERSION } from '../version.js';
import type { ResolvedConfig } from './config.js';
import { unwrapEnvelope, unwrapList } from './envelope.js';
import {
  GpmPayConnectionError,
  GpmPayTimeoutError,
  errorFromResponse,
} from './errors.js';
import { buildQuery, type QueryInput } from './query.js';
import {
  backoffDelayMs,
  parseRetryAfter,
  shouldRetry,
  type HttpMethod,
} from './retry.js';
import { AbortError, abortableSleep, linkSignals } from './signal.js';

export const REQUEST_ID_HEADER = 'X-GPMPay-Request-Id';

export interface HttpRequestOptions extends RequestOptions {
  query?: QueryInput;
  body?: unknown;
}

function buildUserAgent(suffix: string | undefined): string {
  const base = `@gpmpay/sdk/${VERSION} (node/${process.version}; ${process.platform})`;
  return suffix === undefined || suffix === '' ? base : `${base} ${suffix}`;
}

function syscallCodeOf(error: unknown): string | undefined {
  const cause = (error as { cause?: unknown } | null)?.cause;
  const code = (cause as { code?: unknown } | null)?.code;
  return typeof code === 'string' ? code : undefined;
}

export class HttpClient {
  private readonly config: ResolvedConfig;
  private readonly userAgent: string;

  constructor(config: ResolvedConfig) {
    this.config = config;
    this.userAgent = buildUserAgent(config.userAgent);
  }

  get baseUrl(): string {
    return this.config.baseUrl;
  }

  /** Issue a request and return the unwrapped `data` payload. */
  async request<T>(
    method: HttpMethod,
    path: string,
    options: HttpRequestOptions = {},
  ): Promise<T> {
    const raw = await this.send(method, path, options);
    return unwrapEnvelope<T>(raw);
  }

  /** Issue a list request and normalize it into a `Page<T>`. */
  async requestList<T>(
    method: HttpMethod,
    path: string,
    options: HttpRequestOptions = {},
  ): Promise<Page<T>> {
    const raw = await this.send(method, path, options);
    return unwrapList<T>(unwrapEnvelope<unknown>(raw));
  }

  private async send(
    method: HttpMethod,
    path: string,
    options: HttpRequestOptions,
  ): Promise<unknown> {
    const url = `${this.config.baseUrl}${path}${buildQuery(options.query)}`;
    const requestId = randomUUID();
    const timeoutMs = options.timeoutMs ?? this.config.timeoutMs;
    const hasBody = options.body !== undefined;

    const headers: Record<string, string> = {
      ...this.config.defaultHeaders,
      Accept: 'application/json',
      'User-Agent': this.userAgent,
      [REQUEST_ID_HEADER]: requestId,
      ...options.headers,
      // Applied last: Authorization is not user-overridable.
      Authorization: `Bearer ${this.config.apiToken}`,
    };
    if (hasBody) headers['Content-Type'] = 'application/json';

    const init: RequestInit = { method, headers };
    if (hasBody) init.body = JSON.stringify(options.body);

    for (let attempt = 0; ; attempt++) {
      const link = linkSignals(options.signal, timeoutMs);
      const startedAt = Date.now();
      this.config.onRequest?.({ method, url, requestId, attempt });

      let response: Response;
      try {
        response = await this.config.fetchImpl(url, {
          ...init,
          signal: link.signal,
        });
      } catch (error) {
        link.dispose();

        if (link.getReason() === 'timeout') {
          if (
            shouldRetry({
              method,
              isConnectionError: true,
              attempt,
              maxRetries: this.config.maxRetries,
            })
          ) {
            await this.waitBeforeRetry(attempt, undefined, options.signal);
            continue;
          }
          throw new GpmPayTimeoutError(
            `Request timed out after ${String(timeoutMs)}ms: ${method} ${path}`,
            timeoutMs,
          );
        }
        if (link.getReason() === 'external') {
          throw new AbortError(`Request aborted by caller: ${method} ${path}`);
        }

        if (
          shouldRetry({
            method,
            isConnectionError: true,
            attempt,
            maxRetries: this.config.maxRetries,
          })
        ) {
          await this.waitBeforeRetry(attempt, undefined, options.signal);
          continue;
        }
        const syscall = syscallCodeOf(error);
        throw new GpmPayConnectionError(
          `Could not reach the GPM Pay API at ${this.config.baseUrl}` +
            (syscall === undefined ? '' : ` (${syscall})`) +
            '. Check the baseUrl and your network connection.',
          error,
          syscall,
        );
      }

      link.dispose();
      this.config.onResponse?.({
        requestId,
        status: response.status,
        durationMs: Date.now() - startedAt,
        attempt,
      });

      if (response.ok) {
        return await parseBody(response);
      }

      const retryAfterSeconds = parseRetryAfter(
        response.headers.get('retry-after'),
      );

      if (
        shouldRetry({
          method,
          status: response.status,
          attempt,
          maxRetries: this.config.maxRetries,
        })
      ) {
        await this.waitBeforeRetry(attempt, retryAfterSeconds, options.signal);
        continue;
      }

      const errorBody = await parseBody(response).catch(() => undefined);
      throw errorFromResponse(
        response.status,
        errorBody,
        requestId,
        retryAfterSeconds === undefined ? {} : { retryAfterSeconds },
      );
    }
  }

  private async waitBeforeRetry(
    attempt: number,
    retryAfterSeconds: number | undefined,
    signal: AbortSignal | undefined,
  ): Promise<void> {
    const delay = backoffDelayMs({
      attempt,
      baseDelayMs: this.config.retryBaseDelayMs,
      retryAfterSeconds,
    });
    if (delay > 0) await abortableSleep(delay, signal);
  }
}

async function parseBody(response: Response): Promise<unknown> {
  if (response.status === 204 || response.status === 205) return undefined;
  if (response.headers.get('content-length') === '0') return undefined;

  const contentType = response.headers.get('content-type') ?? '';
  const text = await response.text();
  if (text === '') return undefined;

  if (contentType.includes('json')) {
    return JSON.parse(text);
  }

  // Not JSON: try anyway (some proxies omit the header), else hand back the raw
  // text so the error mapper can surface e.g. an HTML 502 page usefully.
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}
